import { type Static, Type } from "typebox";

import { DriverLinkScope } from "./enums.js";
import { defineRoute } from "./route.js";
import {
  ErrorEnvelope,
  errors,
  Exact,
  Extend,
  Identifier,
  Nullable,
  Timestamp,
  WithTotal,
} from "./schema.js";

// --- Drivers ----------------------------------------------------------------

export const DriverSummary = Exact({
  archivedAt: Nullable(Timestamp),
  displayName: Type.String(),
  email: Nullable(Type.String()),
  employeeId: Nullable(Type.String()),
  homeLocation: Nullable(Type.String()),
  id: Identifier,
  /** As typed. The E.164 form is on the full driver as phoneNormalized. */
  phone: Nullable(Type.String()),
  /** ISO 3166 country the number belongs to, such as "US". */
  phoneCountry: Nullable(Type.String()),
  /**
   * A number saved before numbers were normalized that could not be read.
   * It is shown as typed until a manager fixes it.
   */
  phoneNeedsReview: Type.Boolean(),
  /** The member account this driver signs in as, when they have one. */
  userId: Nullable(Type.String()),
});
export type DriverSummary = Static<typeof DriverSummary>;

export const DriverListItem = Extend(DriverSummary, {
  activeAccessTokenCount: Type.Integer(),
  assignedVehicleCount: Type.Integer(),
  /**
   * When the driver's newest link that was not turned off runs out, even if
   * that is already past; null when they have none.
   */
  driverLinkExpiresAt: Nullable(Timestamp),
});
export type DriverListItem = Static<typeof DriverListItem>;

export const DriverList = WithTotal(DriverListItem);
export type DriverList = Static<typeof DriverList>;

/**
 * A driver as it is stored, which is what reads and edits return. Manager
 * routes only: `notes` is never part of anything a driver opens.
 */
export const Driver = Extend(DriverSummary, {
  createdAt: Timestamp,
  emailNormalized: Nullable(Type.String()),
  notes: Nullable(Type.String()),
  organizationId: Identifier,
  phoneNormalized: Nullable(Type.String()),
  updatedAt: Timestamp,
});
export type Driver = Static<typeof Driver>;

const DriverFields = {
  displayName: Type.String({ minLength: 1 }),
  email: Nullable(Type.String({ maxLength: 320 })),
  employeeId: Nullable(Type.String({ maxLength: 64 })),
  homeLocation: Nullable(Type.String({ maxLength: 200 })),
  notes: Nullable(Type.String({ maxLength: 4000 })),
  phone: Nullable(Type.String({ maxLength: 64 })),
  /** Read a number typed without a +code as this country's. */
  phoneCountry: Nullable(Type.String({ pattern: "^[A-Z]{2}$" })),
};

const OptionalDriverFields = {
  email: Type.Optional(DriverFields.email),
  employeeId: Type.Optional(DriverFields.employeeId),
  homeLocation: Type.Optional(DriverFields.homeLocation),
  notes: Type.Optional(DriverFields.notes),
  phone: Type.Optional(DriverFields.phone),
  phoneCountry: Type.Optional(DriverFields.phoneCountry),
};

export const createDriver = defineRoute({
  body: Exact({
    displayName: DriverFields.displayName,
    ...OptionalDriverFields,
  }),
  method: "POST",
  path: "/organizations/:organizationId/drivers",
  // 409 when the number or employee ID is another active driver's; 422 when
  // a field cannot be used as given. Both name the field.
  response: { 201: Driver, ...errors(400, 401, 403, 409, 422) },
  tags: ["drivers"],
});

export const listDrivers = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/drivers",
  querystring: Exact({
    includeArchived: Type.Optional(Type.Boolean()),
    offset: Type.Optional(Type.Integer({ minimum: 0 })),
    pageSize: Type.Optional(Type.Integer({ maximum: 100, minimum: 1 })),
  }),
  response: { 200: DriverList, ...errors(401, 403) },
  tags: ["drivers"],
});

export const getDriver = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/drivers/:driverId",
  response: { 200: Driver, ...errors(401, 403, 404) },
  tags: ["drivers"],
});

export const updateDriver = defineRoute({
  body: Exact({
    displayName: Type.Optional(DriverFields.displayName),
    ...OptionalDriverFields,
  }),
  method: "PATCH",
  path: "/organizations/:organizationId/drivers/:driverId",
  response: { 200: Driver, ...errors(400, 401, 403, 404, 409, 422) },
  tags: ["drivers"],
});

export const deactivateDriver = defineRoute({
  method: "DELETE",
  path: "/organizations/:organizationId/drivers/:driverId",
  response: { 200: Driver, ...errors(401, 403, 404, 409) },
  tags: ["drivers"],
});

// --- Driver links, as a manager handles them -------------------------------

export const IssuedDriverLink = Exact({
  expiresAt: Type.String(),
  id: Identifier,
  // Returned exactly once, at creation. It is never readable again.
  token: Type.String(),
});
export type IssuedDriverLink = Static<typeof IssuedDriverLink>;

export const issueDriverLink = defineRoute({
  method: "POST",
  path: "/organizations/:organizationId/drivers/:driverId/access-tokens",
  response: { 201: IssuedDriverLink, ...errors(401, 403, 404) },
  tags: ["driver-links"],
});

export const rotateDriverLink = defineRoute({
  method: "POST",
  path: "/organizations/:organizationId/drivers/:driverId/access-tokens/rotate",
  response: { 200: IssuedDriverLink, ...errors(401, 403, 404) },
  tags: ["driver-links"],
});

export const RevokedDriverLinks = Exact({ revokedCount: Type.Integer() });
export type RevokedDriverLinks = Static<typeof RevokedDriverLinks>;

export const revokeDriverLinks = defineRoute({
  method: "DELETE",
  path: "/organizations/:organizationId/drivers/:driverId/access-tokens",
  response: { 200: RevokedDriverLinks, ...errors(401, 403) },
  tags: ["driver-links"],
});

export const CheckInRequestResult = Exact({
  /** When the next request may be sent, so the button can say so. */
  nextAllowedAt: Nullable(Type.String()),
  sent: Type.Boolean(),
});
export type CheckInRequestResult = Static<typeof CheckInRequestResult>;

export const sendCheckInRequest = defineRoute({
  method: "POST",
  path: "/organizations/:organizationId/drivers/:driverId/check-in-requests",
  response: {
    200: CheckInRequestResult,
    ...errors(401, 403, 404, 422),
    // Two different refusals share this status: the per-driver cooldown,
    // which answers with when the next request is allowed, and a rate
    // limit, which answers with the standard error. A single schema for
    // one of them would serialize the other through the wrong shape.
    429: Type.Union([CheckInRequestResult, ErrorEnvelope]),
  },
  tags: ["driver-links"],
});

// --- Driver links, as the driver opens them --------------------------------

/**
 * The token is the whole credential on these routes, so it is validated
 * for shape before anything looks it up.
 */
export const DriverLinkToken = Type.String({ maxLength: 200, minLength: 20 });

export const DriverLinkSession = Exact({
  driver: Exact({ displayName: Type.String(), id: Identifier }),
  // Deliberately narrow: the driver page shows who they are checking in
  // for, and nothing else about the organization.
  organization: Exact({ name: Type.String() }),
  /** Whether this deployment accepts photo uploads, so the page can offer them. */
  photoUploads: Type.Boolean(),
  scopes: Type.Array(DriverLinkScope),
  vehicles: Type.Array(
    Exact({
      currentOdometerMiles: Nullable(Type.Integer()),
      id: Identifier,
      internalLabel: Type.String(),
      plate: Nullable(Type.String()),
    }),
  ),
});
export type DriverLinkSession = Static<typeof DriverLinkSession>;

export const getDriverLink = defineRoute({
  method: "GET",
  params: Exact({ token: DriverLinkToken }),
  path: "/driver-links/:token",
  response: { 200: DriverLinkSession, ...errors(404, 429) },
  tags: ["driver-links"],
});

export const DriverMeterReading = Exact({
  id: Identifier,
  observedAt: Type.String(),
  odometerMiles: Type.Integer(),
});
export type DriverMeterReading = Static<typeof DriverMeterReading>;

export const recordDriverMeterEntry = defineRoute({
  body: Exact({
    observedAt: Type.String({ format: "date-time" }),
    odometerMiles: Type.Integer({ maximum: 10_000_000, minimum: 0 }),
    vehicleId: Identifier,
  }),
  method: "POST",
  params: Exact({ token: DriverLinkToken }),
  path: "/driver-links/:token/meter-entries",
  response: { 201: DriverMeterReading, ...errors(403, 404, 409, 422, 429) },
  tags: ["driver-links"],
});
